import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  minHeight?: number;
  placeholder?: string;
};

type EditorCommand =
  | "bold"
  | "italic"
  | "underline"
  | "insertUnorderedList"
  | "insertOrderedList"
  | "formatBlock"
  | "createLink"
  | "removeFormat";

function runCommand(command: EditorCommand, value?: string) {
  document.execCommand(command, false, value);
}

export default function RichTextEditor({
  value,
  onChange,
  minHeight = 180,
  placeholder = "Icerik yaz...",
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastValueRef = useRef<string>(value);
  const [mode, setMode] = useState<"visual" | "html">("visual");

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    if (value === lastValueRef.current) {
      return;
    }
    editorRef.current.innerHTML = DOMPurify.sanitize(value || "");
    lastValueRef.current = value;
  }, [value]);

  const emitChange = () => {
    if (!editorRef.current) {
      return;
    }
    const next = editorRef.current.innerHTML;
    lastValueRef.current = next;
    onChange(next);
  };

  const onLink = async () => {
    // Mevcut seçimi sakla — modal açılınca kaybolmaması için
    const selection = window.getSelection();
    const savedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;

    const { isConfirmed, value } = await Swal.fire({
      title: "Bağlantı ekle",
      input: "url",
      inputLabel: "Link URL",
      inputPlaceholder: "https://...",
      showCancelButton: true,
      confirmButtonText: "Ekle",
      cancelButtonText: "Vazgeç",
      reverseButtons: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#64748b",
      customClass: { popup: "rounded-2xl" },
    });

    const url = isConfirmed && typeof value === "string" ? value.trim() : "";
    if (!url) {
      return;
    }

    // Seçimi geri yükle ve linki uygula
    editorRef.current?.focus();
    if (savedRange && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }
    runCommand("createLink", url);
    emitChange();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-300 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-2">
        <button
          type="button"
          className={`rounded border px-2 py-1 text-xs font-semibold ${mode === "visual" ? "border-emerald-500 bg-emerald-500 text-slate-950" : "border-slate-300 bg-white"}`}
          onClick={() => {
            lastValueRef.current = "";
            setMode("visual");
          }}
        >
          Gorsel
        </button>
        <button
          type="button"
          className={`rounded border px-2 py-1 text-xs font-semibold ${mode === "html" ? "border-emerald-500 bg-emerald-500 text-slate-950" : "border-slate-300 bg-white"}`}
          onClick={() => setMode("html")}
        >
          HTML
        </button>

        {mode === "visual" ? (
          <>
            <span className="mx-1 h-5 w-px bg-slate-300" />
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs font-semibold" onClick={() => { runCommand("bold"); emitChange(); }}>B</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs italic" onClick={() => { runCommand("italic"); emitChange(); }}>I</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs underline" onClick={() => { runCommand("underline"); emitChange(); }}>U</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs font-semibold" onClick={() => { runCommand("formatBlock", "h3"); emitChange(); }}>H3</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs" onClick={() => { runCommand("insertUnorderedList"); emitChange(); }}>Madde</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs" onClick={() => { runCommand("insertOrderedList"); emitChange(); }}>Numara</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs" onClick={() => void onLink()}>Link</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs" onClick={() => { runCommand("removeFormat"); emitChange(); }}>Temizle</button>
          </>
        ) : null}
      </div>

      {mode === "visual" ? (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onBlur={emitChange}
          data-placeholder={placeholder}
          className="prose prose-sm max-w-none p-3 text-sm text-slate-900 focus:outline-none"
          style={{ minHeight }}
        />
      ) : (
        <textarea
          value={value}
          onChange={(event) => {
            lastValueRef.current = event.target.value;
            onChange(event.target.value);
          }}
          className="w-full resize-y border-0 p-3 font-mono text-xs text-slate-800 focus:outline-none"
          style={{ minHeight }}
        />
      )}
    </div>
  );
}
